import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { Send, ShieldCheck, User as UserIcon, Loader2, Trash2, RefreshCw, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { WAF_COUNTRY_AMS } from "@/data/waf-region";

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

/**
 * QuoteChatRoom — chat interactif attaché à un devis sauvegardé.
 *
 * Polling toutes les 5 s tant que le panneau est visible. Le partenaire et le
 * reviewer Fortinet voient les mêmes messages, distingués par badge couleur.
 *
 * Props :
 *   quoteId : string — devis ciblé
 *   quote   : object  — snapshot du devis (sert à afficher le pays + le SE assigné)
 *   onReviewStatusChange?: (status) => void  // pour rafraîchir le badge parent
 */
export default function QuoteChatRoom({ quoteId, quote, onReviewStatusChange }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [reviewStatus, setReviewStatus] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [loading, setLoading] = useState(true);
  const lastIsoRef = useRef(null);
  const scrollRef = useRef(null);

  const isReviewer = !!(user?.is_admin || user?.is_fortinet_reviewer);

  const fetchMessages = async (incremental = false) => {
    try {
      const params = incremental && lastIsoRef.current ? { since: lastIsoRef.current } : {};
      const r = await api.get(`/quotes/${quoteId}/chat/messages`, { params });
      const incoming = r.data?.messages || [];
      if (incoming.length) {
        setMessages((prev) => {
          if (!incremental) return incoming;
          // append, dedup by id
          const ids = new Set(prev.map((m) => m.id));
          return [...prev, ...incoming.filter((m) => !ids.has(m.id))];
        });
        lastIsoRef.current = incoming[incoming.length - 1].created_at;
      } else if (!incremental) {
        setMessages([]);
        lastIsoRef.current = null;
      }
      const newStatus = r.data?.review_status || null;
      setReviewStatus(newStatus);
      onReviewStatusChange?.(newStatus);
    } catch (e) {
      // silent on poll failures
      if (!incremental) toast.error("Impossible de charger la conversation");
    } finally {
      if (!incremental) setLoading(false);
    }
  };

  // Initial fetch + 5s polling
  useEffect(() => {
    if (!quoteId) return;
    setLoading(true);
    setMessages([]);
    lastIsoRef.current = null;
    fetchMessages(false);
    const t = setInterval(() => fetchMessages(true), 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await api.post(`/quotes/${quoteId}/chat/messages`, { text: t });
      setText("");
      await fetchMessages(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const requestReview = async () => {
    setRequesting(true);
    try {
      await api.post(`/quotes/${quoteId}/review-request`, {
        message: "Bonjour, j'aimerais qu'un ingénieur Fortinet revoie cette BOM.",
      });
      toast.success("Revue Fortinet demandée — un ingénieur sera notifié.");
      await fetchMessages(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la demande");
    } finally {
      setRequesting(false);
    }
  };

  const resolveReview = async () => {
    setResolving(true);
    try {
      await api.post(`/quotes/${quoteId}/review-resolve`, {
        note: "Revue Fortinet clôturée. La BOM est conforme.",
      });
      toast.success("Revue clôturée");
      await fetchMessages(false);
    } catch (e) {
      toast.error("Échec de la clôture");
    } finally {
      setResolving(false);
    }
  };

  const [resetting, setResetting] = useState(false);

  // SE picker state — based on the customer country attached to the quote.
  // Only "System Engineer" / "Presales" roles are surfaced (Account Managers
  // are already addressed automatically via the "Envoyer aux équipes" mailto).
  const country = quote?.customer_info?.country || "";
  const seOptions = useMemo(() => {
    const team = (country && WAF_COUNTRY_AMS[country]) || [];
    return team.filter((m) => /System Engineer|Presales/i.test(m.role));
  }, [country]);
  const assigned = quote?.assigned_se || null;
  const [savingAssign, setSavingAssign] = useState(false);

  const onAssignSE = async (value) => {
    setSavingAssign(true);
    try {
      const payload = value === "__none__"
        ? { se_email: null }
        : (() => {
            const m = seOptions.find((x) => x.email === value);
            return m
              ? { se_email: m.email, se_name: m.name, se_role: m.role }
              : { se_email: null };
          })();
      await api.post(`/quotes/${quoteId}/assign-se`, payload);
      toast.success(value === "__none__" ? "Allocation SE retirée" : `SE alloué : ${payload.se_name}`);
      onReviewStatusChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'enregistrer le SE");
    } finally {
      setSavingAssign(false);
    }
  };
  const resetChat = async () => {
    if (!window.confirm("Effacer toute la discussion du Salon Fortinet pour ce devis ?\n\nCette action est irréversible et supprime également l'état de revue (demandée / clôturée).")) {
      return;
    }
    setResetting(true);
    try {
      const r = await api.delete(`/quotes/${quoteId}/chat/messages`);
      toast.success(`Salon réinitialisé · ${r.data?.deleted ?? 0} message(s) supprimé(s)`);
      setReviewStatus(null);
      onReviewStatusChange?.(null);
      await fetchMessages(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la réinitialisation");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="quote-chat-room">
      {/* Header / Status bar */}
      <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50/60 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[#D42B1E]" />
          <span className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-700">
            Salon Fortinet · Devis
          </span>
          {reviewStatus === "requested" && (
            <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-sm bg-amber-100 text-amber-900 border border-amber-300" data-testid="chat-status-requested">
              Revue demandée
            </span>
          )}
          {reviewStatus === "resolved" && (
            <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-sm bg-green-100 text-green-900 border border-green-300" data-testid="chat-status-resolved">
              Revue clôturée
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => fetchMessages(false)}
            disabled={loading}
            className="rounded-sm h-7 text-[11px] text-zinc-600 hover:bg-zinc-100 px-2"
            title="Rafraîchir le salon"
            data-testid="chat-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={resetChat}
            disabled={resetting || messages.length === 0}
            className="rounded-sm h-7 text-[11px] text-zinc-600 hover:bg-rose-50 hover:text-rose-700 px-2"
            title="Effacer toute la discussion et l'état de revue"
            data-testid="chat-reset"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          {!isReviewer && reviewStatus !== "requested" && (
            <Button
              size="sm"
              onClick={requestReview}
              disabled={requesting}
              className="rounded-sm bg-[#D42B1E] hover:bg-[#B32218] h-7 text-[11px]"
              data-testid="chat-request-review"
            >
              {requesting ? "Envoi…" : "Demander une revue Fortinet"}
            </Button>
          )}
          {isReviewer && reviewStatus === "requested" && (
            <Button
              size="sm"
              variant="outline"
              onClick={resolveReview}
              disabled={resolving}
              className="rounded-sm h-7 text-[11px] border-green-300 text-green-700 hover:bg-green-50"
              data-testid="chat-resolve-review"
            >
              {resolving ? "Clôture…" : "Marquer la revue résolue"}
            </Button>
          )}
        </div>
      </div>

      {/* SE picker — dropdown filtered by the customer country attached to
          the quote. Hidden if no country is set. */}
      {country && (
        <div className="border-b border-zinc-100 bg-zinc-50/60 px-4 py-2.5 flex items-center gap-2 flex-wrap" data-testid="chat-se-picker-bar">
          <UserCheck className="h-3.5 w-3.5 text-[#D42B1E] shrink-0" />
          <span className="text-[11px] font-semibold tracking-[0.16em] uppercase text-zinc-500 shrink-0">
            System Engineer alloué · {country}
          </span>
          <div className="flex-1 min-w-[220px]">
            <Select
              value={assigned?.email || "__none__"}
              onValueChange={onAssignSE}
              disabled={savingAssign || seOptions.length === 0}
            >
              <SelectTrigger className="h-8 rounded-sm bg-white text-xs" data-testid="chat-se-select">
                <SelectValue placeholder={seOptions.length ? "Choisir un SE…" : "Aucun SE référencé"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" data-testid="chat-se-option-none">
                  — Aucun (retirer l'allocation)
                </SelectItem>
                {seOptions.map((m) => (
                  <SelectItem key={m.email} value={m.email} data-testid={`chat-se-option-${m.email}`}>
                    <span className="font-medium">{m.name}</span>
                    <span className="text-xs text-zinc-500"> · {m.role}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {assigned?.email && (
            <span className="text-[11px] text-zinc-500 font-mono truncate max-w-[160px]" title={assigned.email}>
              {assigned.email}
            </span>
          )}
        </div>
      )}

      {/* Message list */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-white"
        data-testid="chat-messages-list"
      >
        {loading ? (
          <div className="flex items-center justify-center text-sm text-zinc-500 py-8">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Chargement…
          </div>
        ) : messages.length === 0 ? (
          <p className="text-center text-sm text-zinc-500 py-8">
            Aucun message pour l'instant. Démarrez la conversation pour solliciter une revue Fortinet.
          </p>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === user?.id;
            const isReviewerMsg = m.sender_role === "reviewer";
            return (
              <div
                key={m.id}
                className={`flex gap-2 ${mine ? "flex-row-reverse" : "flex-row"}`}
                data-testid={`chat-message-${m.id}`}
              >
                <div className={`shrink-0 h-8 w-8 rounded-sm flex items-center justify-center ${
                  isReviewerMsg ? "bg-[#D42B1E] text-white" : "bg-zinc-200 text-zinc-700"
                }`}>
                  {isReviewerMsg ? <ShieldCheck className="h-4 w-4" /> : <UserIcon className="h-4 w-4" />}
                </div>
                <div className={`max-w-[75%] rounded-sm px-3 py-2 text-sm ${
                  mine
                    ? (isReviewerMsg ? "bg-[#D42B1E] text-white" : "bg-zinc-900 text-white")
                    : (isReviewerMsg ? "bg-[#D42B1E]/10 border border-[#D42B1E]/20 text-zinc-900" : "bg-zinc-100 text-zinc-900")
                }`}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-[10px] font-semibold uppercase tracking-wide ${mine ? "opacity-80" : "opacity-60"}`}>
                      {isReviewerMsg ? "Fortinet" : "Partenaire"} · {m.sender_name || m.sender_email}
                    </span>
                  </div>
                  <p className="leading-relaxed whitespace-pre-wrap">{m.text}</p>
                  <p className={`mt-1 text-[10px] font-mono ${mine ? "opacity-70" : "opacity-50"}`}>
                    {fmtTime(m.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Input bar */}
      <div className="border-t border-zinc-200 p-3 bg-zinc-50/30">
        <div className="flex items-center gap-2">
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={isReviewer ? "Réponse à l'utilisateur…" : "Posez votre question à Fortinet…"}
            className="rounded-sm flex-1"
            data-testid="chat-message-input"
          />
          <Button
            onClick={send}
            disabled={sending || !text.trim()}
            className="rounded-sm bg-[#D42B1E] hover:bg-[#B32218]"
            data-testid="chat-send-button"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-[10px] text-zinc-400 font-mono mt-1.5">
          Polling 5 s · Entrée pour envoyer · Shift+Entrée pour nouvelle ligne
        </p>
      </div>
    </div>
  );
}
